/**
 * The Vault's wire contract.
 *
 * Three of these constants are security boundaries rather than preferences,
 * and they are here — in the shared package both apps and the server import —
 * because the failure mode is a client and a server disagreeing about what is
 * allowed:
 *
 *  - `ACCEPTED_MIME_TYPES` is an allowlist, not a blocklist. A blocklist of
 *    dangerous types is a list of the ones somebody thought of.
 *  - The OOXML limits are a pair, and BOTH must hold:
 *    `expanded <= min(250MB, compressed * 20)`. Either alone passes a zip bomb
 *    — a small archive expanding 500x is under the absolute cap, and a large
 *    one expanding 3x is under the ratio.
 *  - The size caps differ per category on purpose. A 50MB receipt is not a
 *    receipt.
 *
 * And one thing is deliberately absent: there is no schema anywhere in this
 * file that accepts an object key from a client. Reservation returns the key
 * the server chose, finalize takes the reservation id, and a download is
 * authorized against the document before anything is signed.
 */
import { z } from 'zod';
import { CursorPageQuery, EntityId, IsoDatetime, UserId } from './primitives.js';
import { StorageCadence } from './storage.js';

export const DocumentCategory = z.enum(['document', 'task_media', 'proof', 'receipt', 'voice']);
export type DocumentCategory = z.infer<typeof DocumentCategory>;

export const DocumentLifecycle = z.enum(['active', 'trashed', 'purged']);
export type DocumentLifecycle = z.infer<typeof DocumentLifecycle>;

/**
 * The upload state machine. `security_ready_original` is the only state that
 * may ever make bytes readable; `ready_with_derivative_error` says a preview
 * or transform failed afterwards and the clean original is still fine.
 */
export const VaultVersionState = z.enum([
  'reserved',
  'uploaded_to_quarantine',
  'head_verified',
  'type_validated',
  'malware_scanning',
  'security_ready_original',
  'derivative_processing',
  'ready',
  'ready_with_derivative_error',
  'rejected',
  'delete_pending',
  'deleted',
]);
export type VaultVersionState = z.infer<typeof VaultVersionState>;

/** The states in which a reader may be handed bytes. Exported as data so no
 *  screen re-derives it and gets `derivative_processing` wrong. */
export const READABLE_VERSION_STATES: readonly VaultVersionState[] = [
  'security_ready_original',
  'ready',
  'ready_with_derivative_error',
];

export const VaultVersionSource = z.enum(['upload', 'officecli', 'sanitized_copy', 'backfill']);
export type VaultVersionSource = z.infer<typeof VaultVersionSource>;

// ---------------------------------------------------------------------------
// What may be uploaded. Allowlist, magic bytes, and two independent OOXML
// limits. See the module header for why each one is shaped this way.
// ---------------------------------------------------------------------------

export const ACCEPTED_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
] as const;

export const ACCEPTED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

export const ACCEPTED_MIME_TYPES = [
  ...ACCEPTED_DOCUMENT_MIME_TYPES,
  ...ACCEPTED_IMAGE_MIME_TYPES,
] as const;

/**
 * Named so a rejection can say why without naming a scanner.
 *
 * Legacy binary Office and macro-enabled Office are rejected at launch; so are
 * archives, executables, HTML and SVG (both of which execute), embedded OLE,
 * encrypted documents and video. This list is documentation of the decision,
 * not the mechanism — the mechanism is that anything outside the allowlist
 * above is refused.
 */
export const REJECTED_AT_LAUNCH = [
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.ms-word.document.macroEnabled.12',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
  'application/zip',
  'application/x-rar-compressed',
  'application/x-7z-compressed',
  'text/html',
  'image/svg+xml',
] as const;

export const UPLOAD_LIMITS = {
  /** Office and PDF. A 50MB upload cap is not a promise that a 50MB Office
   *  file is safe to process — the OOXML limits below decide that. */
  officeOrPdfBytes: 50 * 1_000_000,
  generalImageBytes: 15 * 1_000_000,
  receiptBytes: 10 * 1_000_000,
  voiceBytes: 25 * 1_000_000,
  voiceMaxSeconds: 300,
  versionsPerDocument: 50,
  retainedObjectsPerHouseholdSelfService: 25_000,
  /** Both OOXML rules must pass: expanded <= min(250MB, compressed * 20). */
  ooxmlMaxArchiveEntries: 10_000,
  ooxmlMaxExpandedBytes: 250 * 1_000_000,
  ooxmlMaxExpansionRatio: 20,
  reservationTtlSeconds: 300,
  reservationsPerMinutePerHousehold: 20,
  reservationsPerDayPerHousehold: 500,
  readUrlsPerMinutePerHousehold: 120,
  concurrentProcessingJobsPerHousehold: 3,
  officecliTimeoutSeconds: 60,
  officecliMemoryMb: 1024,
  trashRetentionDays: 30,
} as const;

/** The upload cap for a category, in bytes. One function so a screen's
 *  "file too large" and the server's rejection cannot disagree. */
export function maxBytesFor(category: DocumentCategory): number {
  switch (category) {
    case 'receipt':
      return UPLOAD_LIMITS.receiptBytes;
    case 'voice':
      return UPLOAD_LIMITS.voiceBytes;
    case 'proof':
    case 'task_media':
      return UPLOAD_LIMITS.generalImageBytes;
    default:
      return UPLOAD_LIMITS.officeOrPdfBytes;
  }
}

/**
 * Both OOXML rules, as one predicate, so neither is checked without the other.
 * A small archive expanding 500x passes the absolute cap; a large one
 * expanding 3x passes the ratio. Only the conjunction is safe.
 */
export function ooxmlExpansionWithinLimits(
  compressedBytes: number,
  expandedBytes: number,
): boolean {
  const ceiling = Math.min(
    UPLOAD_LIMITS.ooxmlMaxExpandedBytes,
    compressedBytes * UPLOAD_LIMITS.ooxmlMaxExpansionRatio,
  );
  return expandedBytes <= ceiling;
}

// ---------------------------------------------------------------------------
// Requests.
// ---------------------------------------------------------------------------

/**
 * `POST /v1/vault/reservations`.
 *
 * The client declares a filename, a type, an exact byte count and a category,
 * and gets back a five-minute presigned PUT to a quarantine key it did not
 * choose. Every declared value is recorded and none of it is trusted:
 * HeadObject checks the real size at finalize and magic-byte detection decides
 * the real type.
 */
export const CreateReservationRequest = z.object({
  filename: z.string().trim().min(1).max(255),
  declared_mime: z.string().trim().min(1).max(255),
  bytes: z.number().int().positive(),
  category: DocumentCategory,
  /** Stable across retries of the same intent. Returns the same reservation
   *  rather than a second hold on the same bytes. */
  idempotency_key: z.string().min(8).max(128),
  /** Attach on finalize instead of in a second round trip. */
  link: z
    .object({
      target_kind: z.enum([
        'operation',
        'operation_proof',
        'receipt',
        'receipt_scan',
        'expense',
        'note',
        'staff_message',
      ]),
      target_id: z.string().max(40),
    })
    .optional(),
});
export type CreateReservationRequest = z.infer<typeof CreateReservationRequest>;

export const ReservationResponse = z.object({
  reservation_id: EntityId.storageReservation,
  /** Where to PUT. Opaque, short-lived, and scoped to a quarantine prefix the
   *  reader path cannot serve from. */
  upload_url: z.string().url(),
  expires_at: IsoDatetime,
  /** Headers the PUT must send exactly, so the object carries the metadata
   *  HeadObject verifies. */
  required_headers: z.record(z.string()),
});
export type ReservationResponse = z.infer<typeof ReservationResponse>;

/**
 * `POST /v1/vault/reservations/:id/finalize`.
 *
 * Takes the reservation id and nothing else that names bytes. A caller-supplied
 * object key is never accepted here — that is the whole reason the reservation
 * exists.
 */
export const FinalizeReservationRequest = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  /** Adds a version to an existing document instead of creating one. */
  document_id: EntityId.document.optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
});
export type FinalizeReservationRequest = z.infer<typeof FinalizeReservationRequest>;

export const DocumentVersionView = z.object({
  id: EntityId.documentVersion,
  sequence: z.number().int().positive(),
  bytes: z.number().int().nonnegative(),
  sha256: z.string().length(64).nullable(),
  detected_mime: z.string().nullable(),
  filename: z.string().nullable(),
  source: VaultVersionSource,
  state: VaultVersionState,
  /** True when this version's bytes may be served. Sent rather than derived so
   *  a client cannot get the state list wrong. */
  readable: z.boolean(),
  preview_state: z.string().nullable(),
  preview_error: z.string().nullable(),
  rejection_reason: z.string().nullable(),
  created_at: IsoDatetime,
  published_at: IsoDatetime.nullable(),
});
export type DocumentVersionView = z.infer<typeof DocumentVersionView>;

export const DocumentView = z.object({
  id: EntityId.document,
  title: z.string(),
  category: DocumentCategory,
  lifecycle: DocumentLifecycle,
  pinned: z.boolean(),
  tags: z.array(z.string()),
  bytes: z.number().int().nonnegative(),
  current_version: DocumentVersionView.nullable(),
  version_count: z.number().int().nonnegative(),
  /** Who added it. Needed on the row and not only as a filter: a worker may
   *  delete their own unlinked draft, and no client can identify one without
   *  this. Nullable because `documents.created_by_user_id` is ON DELETE SET
   *  NULL — a document outlives the person who uploaded it, and pretending
   *  otherwise would make every such row fail validation. */
  uploaded_by: UserId.nullable(),
  /** The optimistic-concurrency token. A PATCH that sends a stale one is
   *  refused rather than applied, which is what turns a lost update into a
   *  "changed elsewhere" prompt. */
  version_token: z.number().int().positive(),
  trashed_at: IsoDatetime.nullable(),
  /** The exact date this will be purged. Shown before anyone trashes a file,
   *  because a Recovery Vault deadline can fall before the 30-day window. */
  purge_after: IsoDatetime.nullable(),
  links: z.array(
    z.object({
      target_kind: z.string(),
      target_id: z.string(),
      tombstoned_at: IsoDatetime.nullable(),
    }),
  ),
  shared_with: z.array(UserId),
  created_at: IsoDatetime,
  updated_at: IsoDatetime,
});
export type DocumentView = z.infer<typeof DocumentView>;

export const UpdateDocumentRequest = z
  .object({
    title: z.string().trim().min(1).max(300),
    tags: z.array(z.string().trim().min(1).max(40)).max(20),
    pinned: z.boolean(),
  })
  .partial()
  .extend({
    /** Required: an edit that does not say which version it is editing cannot
     *  be refused when it is stale. */
    version_token: z.number().int().positive(),
  });
export type UpdateDocumentRequest = z.infer<typeof UpdateDocumentRequest>;

export const ListDocumentsQuery = CursorPageQuery.extend({
  category: DocumentCategory.optional(),
  lifecycle: DocumentLifecycle.optional(),
  uploader: UserId.optional(),
  q: z.string().trim().max(200).optional(),
  /** The cleanup filters the commercial document names, in its own order of
   *  usefulness. `duplicates` is exact SHA-256 equality, never a similarity
   *  guess — offering to delete something that only looks the same is how a
   *  cleanup screen deletes the wrong file. */
  filter: z
    .enum(['largest', 'oldest', 'unlinked', 'duplicates', 'old_versions', 'trash'])
    .optional(),
});
export type ListDocumentsQuery = z.infer<typeof ListDocumentsQuery>;

export const ShareDocumentRequest = z.object({ user_id: UserId });
export type ShareDocumentRequest = z.infer<typeof ShareDocumentRequest>;

export const DeletionRequestBody = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type DeletionRequestBody = z.infer<typeof DeletionRequestBody>;

/** `POST /v1/vault/documents/:id/download-url`. The ACL is authorized before
 *  anything is signed, and the URL is short-lived and single-purpose. */
export const DownloadUrlResponse = z.object({
  url: z.string().url(),
  expires_at: IsoDatetime,
  filename: z.string().nullable(),
  bytes: z.number().int().nonnegative(),
});
export type DownloadUrlResponse = z.infer<typeof DownloadUrlResponse>;

/**
 * `GET /v1/vault/recovery`. What an ending grant still holds, and by when.
 *
 * `deadline_at` is never hidden and export is never conditional on renewal —
 * both are explicit prohibitions in the commercial document, and both are
 * properties of this shape rather than of a screen.
 */
export const RecoveryVaultView = z.object({
  grants: z.array(
    z.object({
      grant_id: EntityId.storageGrant,
      capacity_bytes: z.number().int().nonnegative(),
      assigned_bytes: z.number().int().nonnegative(),
      cadence: StorageCadence.nullable(),
      paid_through_at: IsoDatetime.nullable(),
      deadline_at: IsoDatetime.nullable(),
      documents: z.array(
        z.object({
          document_id: EntityId.document,
          title: z.string(),
          bytes: z.number().int().nonnegative(),
        }),
      ),
    }),
  ),
});
export type RecoveryVaultView = z.infer<typeof RecoveryVaultView>;

/**
 * Refusals a Vault call can return, as codes.
 *
 * `NOT_FOUND` is what an unauthorized document id, a guessed object id and a
 * cross-household lookup all get. There is no `FORBIDDEN` for object identity
 * on purpose: a 403 confirms the object exists.
 */
export const VaultErrorCode = z.enum([
  'NOT_FOUND',
  'UNSUPPORTED_FORMAT',
  'FILE_TOO_LARGE',
  'MALWARE_DETECTED',
  'SCAN_UNAVAILABLE',
  'STRUCTURE_INVALID',
  'VERSION_LIMIT_REACHED',
  'VERSION_CONFLICT',
  'OWNER_ONLY',
  'DELETION_REQUIRES_OWNER',
  /** Nothing landed at the reserved key. Retry the upload. */
  'UPLOAD_MISSING',
  /** Something landed, but its metadata or prefix did not match the
   *  reservation. Do not retry blindly — the PUT dropped required headers. */
  'UPLOAD_UNVERIFIED',
  /** The `vault_v1` kill switch refusing a byte-producing action while read,
   *  download, Trash, restore and delete keep working. A client that cannot
   *  tell this from a permission error shows the wrong message during a
   *  rollback, and "you are not allowed" is the wrong thing to tell somebody
   *  whose Vault is merely paused. */
  'VAULT_DISABLED',
]);
export type VaultErrorCode = z.infer<typeof VaultErrorCode>;

// ---------------------------------------------------------------------------
// The durable-queue contract between the API and the Vault worker.
// ---------------------------------------------------------------------------

/**
 * Queue names. The version suffix is not decoration: a payload change ships as
 * a new queue, so jobs written by the previous deploy are still readable by the
 * code that drains them.
 *
 * These live here rather than in the worker module because the producer (the
 * API, which enqueues) and the consumer (the Vault worker, which drains) have
 * to agree through a contract. Importing the worker module for a queue name
 * would make a route depend on a worker's implementation — and would pull in a
 * file whose bottom half boots a queue when it is the process entrypoint.
 */
export const VAULT_QUEUES = {
  process: 'famanage.vault.process.v1',
  derivative: 'famanage.vault.derivative.v1',
  officeEdit: 'famanage.vault.office_edit.v1',
  delete: 'famanage.vault.delete.v1',
  reconcile: 'famanage.vault.reconcile.v1',
  dead: 'famanage.vault.dead.v1',
} as const;

/** Admission for one uploaded version. */
export const VaultProcessJob = z
  .object({
    household_id: z.string().min(1).max(40),
    version_id: z.string().min(1).max(40),
    reservation_id: z.string().min(1).max(40).nullable().optional(),
  })
  .strict();
export type VaultProcessJob = z.infer<typeof VaultProcessJob>;

/** Optional derivative work for a version that is already security-ready. */
export const VaultDerivativeJob = z
  .object({
    household_id: z.string().min(1).max(40),
    version_id: z.string().min(1).max(40),
  })
  .strict();
export type VaultDerivativeJob = z.infer<typeof VaultDerivativeJob>;

/**
 * Durable deletion of one object.
 *
 * `release_bytes` is present only when this object's bytes were committed to
 * the ledger, and the release is written after R2 confirms the object is gone
 * — never before, and never twice, because `ledger_key` is unique in the
 * database. A quarantine object was never committed, so it carries no release.
 */
export const VaultDeleteJob = z
  .object({
    household_id: z.string().min(1).max(40),
    bucket: z.enum(['quarantine', 'vault']),
    object_key: z.string().min(1).max(1024),
    version_id: z.string().min(1).max(40).nullable().optional(),
    reason: z.enum([
      'rejected',
      'quarantine_cleanup',
      'reservation_expired',
      'orphan',
      'purge',
      'derivative_discarded',
    ]),
    release_bytes: z.number().int().nonnegative().nullable().optional(),
    ledger_key: z.string().min(1).max(200).nullable().optional(),
  })
  .strict();
export type VaultDeleteJob = z.infer<typeof VaultDeleteJob>;

/**
 * Apply a typed edit plan and publish the result as a new version.
 *
 * The plan is the only model-influenced field, and the worker re-validates it
 * rather than trusting it, because it has been through a queue: a payload read
 * back out of a database table is as much an input as an HTTP body.
 */
export const VaultOfficeEditJob = z
  .object({
    household_id: z.string().min(1).max(40),
    document_id: z.string().min(1).max(40),
    source_version_id: z.string().min(1).max(40),
    actor_user_id: z.string().min(1).max(40).nullable().optional(),
    /** Stable per intent. Two deliveries of the same edit publish one version. */
    idempotency_key: z.string().min(8).max(128),
    plan: z.unknown(),
  })
  .strict();
export type VaultOfficeEditJob = z.infer<typeof VaultOfficeEditJob>;
